import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  PasswordComplexityNotice,
  TextInput,
} from 'shared/components';
import { setLicenseUserPassword } from 'orders/OrderContainer/actions';
import { validatePasswordComplexity } from 'shared/validation';

const OrderManagerPasswordForm = ({ closeCb, refetchManagers, manager }) => {
  const { t } = useTranslation();
  const managerToEditID = get(manager, 'id');

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  const handlePasswordChange = (val) => {
    setDirty(true);
    setPassword(val);
    setPasswordErrors(validatePasswordComplexity(val));
  };

  const validateForm = () => {
    const errors = validatePasswordComplexity(password);
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (!isValid || isLoading) {
      return false;
    }

    setLoading(true);
    const data = { password };

    setLicenseUserPassword(managerToEditID, data)
      .then(() => {
        refetchManagers();
        closeCb();
        Notification('success', t('Changes saved successfully'));
      })
      .catch((err) => {
        setLoading(false);
        const passwordError = err?.response?.data?.password;
        if (passwordError) {
          Notification('error', t('Password validation error'), passwordError);
        } else {
          Notification(
            'error',
            t('Your changes were not saved'),
            t('There was an error while saving your changes'),
          );
        }
      });
    return true;
  };

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }
    return setDirtyFormDisplay(true);
  };

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={handleClose}
      disabled={isLoading}
      size="sm"
      title={t('Change password')}
    >
      <form className="OrderManagerPasswordForm" onSubmit={handleSubmit}>
        <div className="form-row">
          <Label text={t('Email')} inputId="email" />
          <div className="TextInput">
            <input type="text" value={get(manager, 'true_email')} disabled />
          </div>
        </div>
        <div className="form-row">
          <Label text={t('Password')} inputId="password" />
          <TextInput
            id="password"
            value={password}
            error={passwordErrors.join(', ')}
            handleChange={handlePasswordChange}
          />
        </div>
        <PasswordComplexityNotice />
      </form>
      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

OrderManagerPasswordForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  refetchManagers: PropTypes.func.isRequired,
  manager: PropTypes.object.isRequired,
};

export default OrderManagerPasswordForm;
